import io, { Socket } from 'socket.io-client';

type EventListener = (data: any) => void;

class WebSocketService {
  private socket: Socket | null = null;
  private url: string;
  private accessToken: string | null = null;
  private listeners: Map<string, Set<EventListener>> = new Map();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;

  private static getDefaultSocketURL(): string {
    if (typeof window !== 'undefined' && window.location?.hostname) {
      const { hostname, origin, port, protocol } = window.location;
      if (port && port !== '5000') {
        const socketProtocol = protocol === 'https:' ? 'https' : 'http';
        return `${socketProtocol}://${hostname}:5000`;
      }
      if (!port || port === '5000') {
        return origin;
      }
    }
    return 'http://localhost:5000';
  }

  constructor(
    url: string = process.env.REACT_APP_API_URL || WebSocketService.getDefaultSocketURL()
  ) {
    this.url = url;
  }

  connect(token?: string): Promise<void> {
    const accessToken = token || (
      typeof window !== 'undefined' ? window.localStorage.getItem('access_token') || undefined : undefined
    );

    if (!accessToken) {
      this.disconnect();
      return Promise.reject(new Error('WebSocket authentication requires an access token'));
    }

    if (this.socket && this.accessToken === accessToken) {
      if (this.socket.connected) return Promise.resolve();
      return new Promise((resolve, reject) => {
        this.socket?.once('connect', () => resolve());
        this.socket?.once('connect_error', reject);
        this.socket?.connect();
      });
    }

    this.disconnect();
    this.accessToken = accessToken;

    return new Promise((resolve, reject) => {
      const options: any = {
        reconnection: true,
        reconnectionDelay: this.reconnectDelay,
        reconnectionDelayMax: 5000,
        reconnectionAttempts: this.maxReconnectAttempts,
        auth: { token: accessToken },
      };

      this.socket = io(this.url, options);

      this.listeners.forEach((_listeners, event) => {
        this.bindSocketEvent(event);
      });

      this.socket.on('connect', () => {
        console.log('WebSocket connected');
        this.reconnectAttempts = 0;
        this.notifyListeners('ws:connected', null);
        resolve();
      });

      this.socket.on('disconnect', () => {
        console.log('WebSocket disconnected');
        this.notifyListeners('ws:disconnected', null);
      });

      this.socket.on('connect_error', (error: any) => {
        console.error('WebSocket connection error:', error);
        this.notifyListeners('ws:error', error);
        if (/auth|token/i.test(String(error?.message || ''))) {
          this.socket?.disconnect();
        }
        if (this.reconnectAttempts === 0) {
          reject(error);
        }
        this.reconnectAttempts++;
      });

      this.socket.on('error', (error: any) => {
        console.error('WebSocket error:', error);
        this.notifyListeners('ws:error', error);
      });
    });
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.accessToken = null;
    this.reconnectAttempts = 0;
  }

  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  on(event: string, listener: EventListener) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
      this.bindSocketEvent(event);
    }

    this.listeners.get(event)!.add(listener);

    // Return unsubscribe function
    return () => this.off(event, listener);
  }

  off(event: string, listener: EventListener) {
    const listeners = this.listeners.get(event);
    if (listeners) {
      listeners.delete(listener);
      if (listeners.size === 0) {
        this.listeners.delete(event);
      }
    }
  }

  private notifyListeners(event: string, data: any) {
    const listeners = this.listeners.get(event);
    if (listeners) {
      listeners.forEach((listener) => {
        try {
          listener(data);
        } catch (error) {
          console.error(`Error in listener for event ${event}:`, error);
        }
      });
    }
  }

  // Subscribe to real-time events
  subscribeToSchoolUpdates(listener: EventListener) {
    if (this.socket) {
      this.socket.emit('subscribe', { channel: 'schools' });
    }
    return this.on('update:schools', listener);
  }

  subscribeToProjectUpdates(listener: EventListener) {
    if (this.socket) {
      this.socket.emit('subscribe', { channel: 'projects' });
    }
    return this.on('update:projects', listener);
  }

  subscribeToVerificationUpdates(listener: EventListener) {
    if (this.socket) {
      this.socket.emit('subscribe', { channel: 'verification' });
    }
    return this.on('update:verification', listener);
  }

  subscribeToHistoryUpdates(listener: EventListener) {
    if (this.socket) {
      this.socket.emit('subscribe', { channel: 'history' });
    }
    return this.on('update:history', listener);
  }

  private bindSocketEvent(event: string) {
    if (!this.socket) return;
    this.socket.off(event);
    this.socket.on(event, (data: any) => {
      this.notifyListeners(event, data);
    });
  }

  subscribeToDispatchUpdates(listener: EventListener) {
    if (this.socket) {
      this.socket.emit('subscribe', { channel: 'dispatches' });
    }
    return this.on('update:dispatches', listener);
  }

  // Emit events
  emit(event: string, data: any) {
    if (this.socket) {
      this.socket.emit(event, data);
    } else {
      console.warn(`Socket not connected. Cannot emit ${event}`);
    }
  }
}

const websocketService = new WebSocketService();
export default websocketService;
